/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */

// pcg-core: Marker-overlap recovery research check (RESEARCH-ONLY; new in the
// v0.8.3 handoff — pcg_viewer_v05_build.py template function
// markerOverlapResearch plus the marker-overlap preview head of visibleEnergyGuide).
//
// The check asks whether repeatable acoustic activity may be hiding inside the
// provisional S1/S2 marker windows that the coherent comparison deliberately
// excludes. It never repositions a marker and never changes a released output;
// its only products are a research classification, an audit block, and a
// display series that the Energy layer may overlay while the preview is active.
//
// Extraction discipline matches the rest of pcg-core (see MODULE-MAP.md):
// function bodies are verbatim modulo the systematic global->argument edits —
//   D                          -> ctx.payload
//   D._markerOverlapResearch   -> ctx.cache.markerOverlapResearch (per-band memo)
//   D.source                   -> ctx.payload.source ?? null
//   window.__medaicaCurrentRecordingId -> ctx.currentRecordingId
//   analysisQuality()          -> analysisQuality(ctx)
//   coherentEnergyV1(bandKey)  -> coherentEnergyV1(ctx, bandKey)
//   state.energyBand           -> explicit bandKey argument
//   state.markerOverlapPreview -> explicit markerOverlapPreview argument (visibleEnergyGuide)
//   valuesForWindow(t,a,z)     -> valuesForWindow(t,a,z,duration)
//
// Prototype v0.8.3.18 (pcg-viewer-mvp git 6b385e4): the check compares each
// marker window against a per-sound reference — a median S1 template and a
// median S2 template from the self-calibrated reference windows, each with a
// ceiling of the median plus 1.75 median absolute deviations — and only the
// residual above that ceiling counts as overlap evidence or is drawn. Without
// both templates there is no residual reference, the full gate cannot pass and
// the display series is empty. In Overlap mode the Energy layer shows that
// qualified residual alone: ordinary Energy and the expected S1/S2 envelope
// are not drawn.
//
// GUARDRAIL: everything in this module is research-only. It feeds the research
// triage surface and the Energy layer's opt-in overlap preview; it must never
// feed Provisional Results values or the export builder's released fields.

import { mean, median, sd, percentile, valuesForWindow } from "./stats"
import { cycleAuditId } from "./cycles"
import { analysisQuality } from "./analysis-quality"
import { coherentEnergyV1, coherentEnergyGuide } from "./coherent-energy"

export function markerOverlapResearch(ctx, bandKey = "s100") {
  ctx.cache.markerOverlapResearch = ctx.cache.markerOverlapResearch || {}
  if (ctx.cache.markerOverlapResearch[bandKey]) return ctx.cache.markerOverlapResearch[bandKey]
  const D = ctx.payload
  const coherent = coherentEnergyV1(ctx, bandKey)
  const trace = coherent.backgroundSeries || new Float32Array(0)
  const quality = analysisQuality(ctx)
  const cycles = quality.summaryCycles || []
  const unavailable = (reason) => ({
    schema: "medaica.pcg.marker_overlap_recovery.research.v0.1",
    researchOnly: true,
    releasedOutputsChanged: false,
    status: "unavailable",
    reason,
    series: new Float32Array(trace.length),
    activity: "Not established",
    phaseAssignment: "Unassigned",
    localizationIssue: "Not assessed",
    scoredMarkerWindows: 0,
    qualifyingMarkerWindows: 0,
    recoveredDisplayPct: 0,
    triggerReasons: [],
    reviewAvailable: false,
  })
  if (!trace.length || !D.duration || cycles.length < 3) {
    const result = unavailable("At least three accepted Timing cycles and a selected-band envelope are required")
    ctx.cache.markerOverlapResearch[bandKey] = result
    return result
  }
  // Evidence and localization remain separate. Marker timestamps define the
  // comparison windows, but the unsuppressed selected-band envelope supplies
  // all acoustic evidence. Nothing in this function changes a marker.
  const markers = []
  const seen = new Set()
  cycles.forEach((c) =>
    [
      { marker: c.s1, label: "S1", cycleId: cycleAuditId(c) },
      { marker: c.s2, label: "S2", cycleId: cycleAuditId(c) },
    ].forEach((item) => {
      const key = `${item.label}:${Number(item.marker.t).toFixed(4)}`
      if (seen.has(key)) return
      seen.add(key)
      const nominalHalf = item.label === "S1" ? 0.095 : 0.075
      const start = Math.max(0, Math.min(item.marker.on ?? item.marker.t - nominalHalf, item.marker.t - nominalHalf))
      const end = Math.min(
        D.duration,
        Math.max(item.marker.off ?? item.marker.t + nominalHalf, item.marker.t + nominalHalf)
      )
      markers.push({ ...item, start, end, confidence: Number(item.marker.confidence ?? 0) })
    })
  )
  const bins = 20
  function binned(start, end) {
    const row = []
    for (let b = 0; b < bins; b++) {
      const a = start + ((end - start) * b) / bins
      const z = start + ((end - start) * (b + 1)) / bins
      const values = valuesForWindow(trace, a, z, D.duration)
      row.push(values.length ? mean(values) : 0)
    }
    return row
  }
  const rows = markers.map((item) => {
    const duration = item.end - item.start
    const flank = Math.max(0.06, 0.55 * duration)
    const flankValues = valuesForWindow(trace, Math.max(0, item.start - flank), item.start, D.duration).concat(
      valuesForWindow(trace, item.end, Math.min(D.duration, item.end + flank), D.duration)
    )
    const row = binned(item.start, item.end)
    const baseline = flankValues.length ? percentile(flankValues, 0.5) : percentile(row, 0.25)
    const spread = Math.max(
      0.025,
      (flankValues.length ? percentile(flankValues, 0.75) : percentile(row, 0.75)) - baseline
    )
    return { ...item, row, baseline, spread }
  })
  const populationPrior = {
    schema: "medaica.pcg.major_heart_sound_population_prior.research.v0.1",
    // Broad research bounds, deliberately not locked clinical thresholds.
    // They screen a proposed reference; they do not diagnose pathology.
    s1DurationFractionOfRr: [0.055, 0.24],
    s2DurationFractionOfRr: [0.035, 0.18],
    maxSustainedOccupancy: 0.55,
    maxUpperBandShare: 0.58,
    note: "Tunable research prior; validate against the control set before normal-view use.",
  }
  function rrForMarker(item) {
    const cycle = cycles.find((c) => cycleAuditId(c) === item.cycleId)
    return cycle && Number.isFinite(cycle.cycle) ? cycle.cycle : null
  }
  function rowMetrics(item) {
    const peak = Math.max(...item.row)
    const threshold = item.baseline + 0.35 * item.spread
    const active = item.row.map((value) => value >= threshold)
    let longest = 0,
      current = 0
    active.forEach((on) => {
      current = on ? current + 1 : 0
      longest = Math.max(longest, current)
    })
    const weightedTotal = item.row.reduce((sum, value) => sum + Math.max(0, value - item.baseline), 0)
    const centreStart = Math.floor(bins * 0.3),
      centreEnd = Math.ceil(bins * 0.7)
    const centreEnergy = item.row
      .slice(centreStart, centreEnd)
      .reduce((sum, value) => sum + Math.max(0, value - item.baseline), 0)
    const rr = rrForMarker(item)
    return {
      markerLabel: item.label,
      cycleId: item.cycleId,
      markerConfidence: item.confidence,
      windowDurationMs: 1000 * (item.end - item.start),
      windowDurationFractionOfRr: rr ? (item.end - item.start) / rr : null,
      sustainedOccupancy: longest / bins,
      centreEnergyShare: weightedTotal ? centreEnergy / weightedTotal : 0,
      normalizedPeak: (peak - item.baseline) / Math.max(0.025, item.spread),
    }
  }
  const perMarkerMetrics = rows.map(rowMetrics)
  const highConfidenceIndexes = perMarkerMetrics.reduce((out, item, index) => {
    if (item.markerConfidence >= 0.72) out.push(index)
    return out
  }, [])
  const selfReferenceIndexes = highConfidenceIndexes.filter((index) => {
    const item = perMarkerMetrics[index]
    const durationBounds =
      item.markerLabel === "S1" ? populationPrior.s1DurationFractionOfRr : populationPrior.s2DurationFractionOfRr
    const durationOkay =
      item.windowDurationFractionOfRr !== null &&
      item.windowDurationFractionOfRr >= durationBounds[0] &&
      item.windowDurationFractionOfRr <= durationBounds[1]
    return (
      durationOkay && item.sustainedOccupancy <= populationPrior.maxSustainedOccupancy && item.centreEnergyShare >= 0.42
    )
  })
  const possibleContamination =
    highConfidenceIndexes.length >= 3 &&
    selfReferenceIndexes.length < Math.max(3, Math.ceil(highConfidenceIndexes.length * 0.45))
  const selfReferenceAvailable =
    selfReferenceIndexes.length >= Math.max(3, Math.ceil(rows.length * 0.3)) && !possibleContamination
  const referenceSource = selfReferenceAvailable ? "self_calibrated" : "population_prior"
  const referenceIndexes = selfReferenceAvailable ? selfReferenceIndexes : []
  const referenceRows = referenceIndexes.map((index) => rows[index])
  const referenceTemplates = {}
  const referenceCeilings = {}
  if (selfReferenceAvailable) {
    ;["S1", "S2"].forEach((label) => {
      const labelRows = referenceRows.filter((item) => item.label === label)
      if (labelRows.length < 2) return
      referenceTemplates[label] = Array.from({ length: bins }, (_, bin) =>
        median(labelRows.map((item) => item.row[bin]))
      )
      referenceCeilings[label] = Array.from({ length: bins }, (_, bin) => {
        const values = labelRows.map((item) => item.row[bin])
        const centre = referenceTemplates[label][bin]
        const mad = median(values.map((value) => Math.abs(value - centre)))
        return centre + Math.max(0.025, 1.75 * mad)
      })
    })
  }
  const residualReferenceAvailable = !!(referenceCeilings.S1 && referenceCeilings.S2)
  const referenceTemplate = residualReferenceAvailable
    ? Array.from({ length: bins }, (_, bin) => 0.5 * (referenceTemplates.S1[bin] + referenceTemplates.S2[bin]))
    : null
  function correlation(a, b) {
    if (!a?.length || a.length !== b?.length) return 0
    const am = mean(a),
      bm = mean(b)
    let dot = 0,
      aa = 0,
      bb = 0
    for (let i = 0; i < a.length; i++) {
      const av = a[i] - am,
        bv = b[i] - bm
      dot += av * bv
      aa += av * av
      bb += bv * bv
    }
    return aa > 1e-9 && bb > 1e-9 ? dot / Math.sqrt(aa * bb) : 0
  }
  const referenceCorrelations = referenceTemplate ? rows.map((item) => correlation(item.row, referenceTemplate)) : []
  const rrNormalizedPeakOffsets = rows
    .map((item) => {
      const rr = rrForMarker(item)
      if (!rr) return null
      const peakIndex = item.row.indexOf(Math.max(...item.row))
      const windowOffset = (peakIndex + 0.5) / bins - 0.5
      return (windowOffset * (item.end - item.start)) / rr
    })
    .filter(Number.isFinite)
  const peakOffsetVariabilityRr = rrNormalizedPeakOffsets.length > 1 ? sd(rrNormalizedPeakOffsets) : null
  const peakOffsetVariabilityMs =
    rows.length > 1
      ? sd(
          rows.map((item) => {
            const peakIndex = item.row.indexOf(Math.max(...item.row))
            return 1000 * ((peakIndex + 0.5) / bins - 0.5) * (item.end - item.start)
          })
        )
      : null
  const durationFractions = perMarkerMetrics.map((item) => item.windowDurationFractionOfRr).filter(Number.isFinite)
  const medianSustainedOccupancy = median(perMarkerMetrics.map((item) => item.sustainedOccupancy))
  const medianReferenceCorrelation = referenceCorrelations.length ? median(referenceCorrelations) : null
  const referenceAudit = {
    schema: "medaica.pcg.marker_overlap_reference.research.v0.1",
    source: referenceSource,
    recording: D.source ?? null,
    site: ctx.currentRecordingId || null,
    referenceCycleIds: referenceIndexes.map((index) => rows[index].cycleId),
    referenceMarkerLabels: referenceIndexes.map((index) => rows[index].label),
    referenceMarkerCount: referenceIndexes.length,
    candidateHighConfidenceMarkerCount: highConfidenceIndexes.length,
    markerConfidenceDistribution: perMarkerMetrics.map((item) => item.markerConfidence),
    possibleContamination,
    contaminationTrigger:
      "candidate reference failed population-prior duration, sustained-occupancy, or impulse-centering screen",
    crossPosition: {
      status: "unavailable",
      reason:
        "Selected-recording browser package does not expose comparable marker-window envelopes from other exam sites.",
    },
    populationPrior,
    selectionReason: selfReferenceAvailable
      ? "High-confidence marker windows passed the population-prior contamination screen."
      : possibleContamination
      ? "Self-reference rejected for possible contamination; population prior retained as the comparison source."
      : "Too few independently trustworthy marker windows for self-calibration; population prior retained.",
  }
  const requiredHits = Math.max(3, Math.ceil(rows.length * 0.35))
  const mask = []
  const recurrence = []
  const residualAt = (item, bin) => {
    const ceiling = referenceCeilings[item.label]?.[bin]
    return Number.isFinite(ceiling) ? Math.max(0, item.row[bin] - ceiling) : 0
  }
  for (let b = 0; b < bins; b++) {
    const hitIndexes = []
    rows.forEach((item, index) => {
      let tolerant = 0
      for (let k = Math.max(0, b - 1); k <= Math.min(bins - 1, b + 1); k++)
        tolerant = Math.max(tolerant, residualAt(item, k))
      if (tolerant >= Math.max(0.025, 0.35 * item.spread)) hitIndexes.push(index)
    })
    const firstThirdEnd = Math.max(0, Math.ceil(rows.length / 3) - 1)
    const lastThirdStart = Math.max(0, Math.floor((2 * rows.length) / 3))
    const span =
      hitIndexes.length > 1 ? (hitIndexes[hitIndexes.length - 1] - hitIndexes[0]) / Math.max(1, rows.length - 1) : 0
    const distributed =
      hitIndexes.some((i) => i <= firstThirdEnd) && hitIndexes.some((i) => i >= lastThirdStart) && span >= 0.55
    recurrence.push(hitIndexes.length / rows.length)
    mask.push(hitIndexes.length >= requiredHits && distributed)
  }
  // Reject an isolated impulse-shaped consensus. A marker-overlap candidate
  // must occupy a sustained run or separated portions of the marker window;
  // a narrow repeatable S1/S2 spike alone is not enough.
  const expanded = mask.map((on, b, arr) => on || (!!arr[b - 1] && !!arr[b + 1]))
  const onBins = expanded.reduce((out, on, index) => {
    if (on) out.push(index)
    return out
  }, [])
  let longestRun = 0,
    run = 0
  expanded.forEach((on) => {
    run = on ? run + 1 : 0
    longestRun = Math.max(longestRun, run)
  })
  const sustained = longestRun >= 4 || (onBins.length >= 6 && onBins[onBins.length - 1] - onBins[0] >= 8)
  const candidateSeries = new Float32Array(trace.length)
  let qualifyingMarkerWindows = 0
  if (sustained) {
    rows.forEach((item) => {
      const localHits = []
      expanded.forEach((on, b) => {
        if (on && residualAt(item, b) >= Math.max(0.025, 0.2 * item.spread)) localHits.push(b)
      })
      if (localHits.length < Math.max(2, Math.floor(onBins.length * 0.35))) return
      qualifyingMarkerWindows += 1
      localHits.forEach((b) => {
        const start = item.start + ((item.end - item.start) * b) / bins
        const end = item.start + ((item.end - item.start) * (b + 1)) / bins
        const i0 = Math.max(0, Math.floor((start / D.duration) * trace.length))
        const i1 = Math.min(trace.length, Math.ceil((end / D.duration) * trace.length))
        const ceiling = referenceCeilings[item.label]?.[b]
        if (!Number.isFinite(ceiling)) return
        for (let i = i0; i < i1; i++) {
          const residual = Math.max(0, (trace[i] || 0) - ceiling)
          if (residual > 0) candidateSeries[i] = residual
        }
      })
    })
  }
  const candidateDisplaySamples = Array.from(candidateSeries).filter((value) => value > 0).length
  const hardSeries = coherent.series || []
  let recoveredSamples = 0
  for (let i = 0; i < candidateSeries.length; i++)
    if (candidateSeries[i] > 0 && !(hardSeries[i] > 0)) recoveredSamples += 1
  const recoveredDisplayPct = candidateDisplaySamples ? (100 * recoveredSamples) / candidateDisplaySamples : 0
  const activity = qualifyingMarkerWindows >= requiredHits ? "Repeatable activity present" : "Not established"
  const localizationIssue = activity === "Repeatable activity present" ? "Marker overlap suspected" : "None established"
  const recurrencePct = rows.length ? (100 * qualifyingMarkerWindows) / rows.length : 0
  const persistenceScore = medianSustainedOccupancy
  const acousticDistinctionScore =
    referenceTemplate && referenceCorrelations.length
      ? Math.max(0, 1 - median(referenceCorrelations.map((value) => Math.max(-1, Math.min(1, value)))))
      : Math.max(0, Math.min(1, (medianSustainedOccupancy - populationPrior.maxSustainedOccupancy) / 0.35))
  let researchClassification = "Unassessable"
  if (referenceSource === "self_calibrated" || referenceSource === "population_prior") {
    if (
      activity === "Repeatable activity present" &&
      persistenceScore > populationPrior.maxSustainedOccupancy &&
      acousticDistinctionScore >= 0.2
    ) {
      researchClassification = "Recurrent sustained-overlap candidate"
    } else if (
      activity === "Repeatable activity present" &&
      (persistenceScore > 0.4 || acousticDistinctionScore >= 0.12)
    ) {
      researchClassification = "Borderline overlap profile"
    } else {
      researchClassification = "Ordinary transient profile"
    }
  }
  const metricsAudit = {
    schema: "medaica.pcg.marker_overlap_metrics.research.v0.1",
    computedWithReferenceSource: referenceSource,
    recurrence: {
      qualifyingMarkerWindows,
      evaluatedMarkerWindows: rows.length,
      recurrencePct,
      requiredMarkerWindowHits: requiredHits,
    },
    persistence: { medianSustainedOccupancy },
    acousticDistinction: {
      score: acousticDistinctionScore,
      medianReferenceCorrelation,
      interpretation: "Descriptive research difference from the selected major-heart-sound reference; not pathology.",
    },
    timingOffset: {
      variabilityFractionOfLocalRr: peakOffsetVariabilityRr,
      variabilityMsAuditOnly: peakOffsetVariabilityMs,
      normalization: "local_rr_interval",
    },
    markerWindowDuration: { medianFractionOfLocalRr: median(durationFractions) },
    perMarker: perMarkerMetrics,
  }
  // The button's illuminated state is itself the finding. Do not promote
  // recurrence alone: ordinary S1/S2 transients are highly recurrent. The
  // full reference, persistence, and acoustic-distinction gate must pass.
  const fullResidualGate =
    residualReferenceAvailable && researchClassification === "Recurrent sustained-overlap candidate"
  // Never render an ungated or ordinary S1/S2 trace in Overlap mode.
  const series = fullResidualGate ? candidateSeries : new Float32Array(trace.length)
  const displaySamples = fullResidualGate ? candidateDisplaySamples : 0
  const standardEnergyOmission = fullResidualGate && displaySamples > 0 && recoveredDisplayPct >= 60
  const triggerReasons = []
  if (fullResidualGate) triggerReasons.push("marker_window_residual")
  if (standardEnergyOmission) triggerReasons.push("standard_energy_omission")
  const result = {
    schema: "medaica.pcg.marker_overlap_recovery.research.v0.1",
    researchOnly: true,
    releasedOutputsChanged: false,
    status: "available",
    rangeHz: coherent.rangeHz,
    series,
    backgroundSeries: trace,
    activity,
    phaseAssignment: "Unassigned",
    localizationIssue,
    researchClassification,
    reference: referenceAudit,
    metrics: metricsAudit,
    scoredTimingCycleIds: coherent.scoredTimingCycleIds || [],
    scoredMarkerWindows: rows.length,
    qualifyingMarkerWindows,
    requiredMarkerWindowHits: requiredHits,
    consensusBinRecurrence: recurrence,
    sustainedConsensus: sustained,
    recoveredDisplayPct,
    triggerReasons,
    reviewAvailable: triggerReasons.length > 0,
    markerTimestampsChanged: false,
    reason:
      activity === "Repeatable activity present"
        ? "The unsuppressed selected-band envelope contains a sustained, distributed pattern within provisional marker windows. This preserves activity existence but does not assign phase or reposition markers."
        : "No sustained marker-window pattern met the research recurrence and temporal-distribution checks.",
  }
  ctx.cache.markerOverlapResearch[bandKey] = result
  return result
}

// Energy-layer display series for the selected sweep band. While the opt-in
// marker-overlap preview is active, the overlay is the marker-overlap research
// series alone — a qualified overlap residual, with ordinary Energy and the
// expected S1/S2 envelope deliberately absent; otherwise this is the
// kernel-blended coherent guide (or the released trace outside coherent mode).
export function visibleEnergyGuide(ctx, bandKey = "s100", energyGuideMode = "coherent", markerOverlapPreview = false) {
  if (markerOverlapPreview) {
    const overlap = markerOverlapResearch(ctx, bandKey)
    return overlap.series || new Float32Array(0)
  }
  return coherentEnergyGuide(ctx, bandKey, energyGuideMode)
}
